// src/oura/ui/PageHead.tsx
//
// The page header: an over-line, the 32px title, one sentence carrying the
// verdict, and the actions on the right.
//
// Merchant screens render <PageHead> themselves. On a staff business screen
// the chrome renders it (BusinessDetail), and the screen fills the sentence
// and adds actions from inside its own tree with <PageSentence> and
// <PageActions>, which portal into the chrome's header. Under the legacy
// chrome (the business sidebar flag off) there is no header to portal into,
// and both render in place at the top of the screen instead.

import { useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { TYPE } from "./tokens";
import { PageSlotContext, type PageSlotRegistry, type PageSlotTargets } from "./pageSlots";

interface PageHeadProps {
    over?: ReactNode;
    title: ReactNode;
    /**
     * What the page IS FOR. A fixed description, never a sentence that changes
     * with the data (#4026 C-FE-6).
     *
     * Every page used to generate this line, so the place a reader looks to
     * learn what a screen does instead told them what was on it today -- and
     * on a quiet account it read "Nothing needs you", which is not a
     * description of anything. The live facts moved to `status`.
     */
    sentence?: ReactNode;
    /**
     * Today's figures: counts, dates, what is pending. Its own row under the
     * description, styled as figures rather than as a subtitle, so a reader
     * can tell the two apart at a glance -- one never changes, the other is
     * the whole point of coming back.
     */
    status?: ReactNode;
    actions?: ReactNode;
}

function Head({ over, title, sentence, status, actions, slots }: PageHeadProps & { slots?: PageSlotRegistry }) {
    return (
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
                {over && <p className={cn(TYPE.lab, "m-0")}>{over}</p>}
                <h1 className={cn(TYPE.h1, "m-0 text-gray-900")}>{title}</h1>
                {sentence && <p className={cn(TYPE.ts, "m-0 mt-1")}>{sentence}</p>}
                {/* Figures, not prose: tabular, darker, and tighter to the
                    title block than the description above it. */}
                {status && <p className={cn(TYPE.tc, "m-0 mt-1.5 font-medium tabular-nums text-gray-700")}>{status}</p>}
                {/* Empty until a screen portals into it, and hidden while empty. */}
                {slots && <div ref={slots.setSentence} className={cn(TYPE.ts, "mt-1 empty:hidden")} />}
            </div>
            {(actions || slots) && (
                // min-w-0: a segmented track in the actions scrolls inside the
                // column on a phone. Without it this row sized itself to the
                // track's full width and pushed the page sideways.
                <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                    {slots && <div ref={slots.setActions} className="contents" />}
                    {actions}
                </div>
            )}
        </header>
    );
}

/** The header for a screen that owns its own chrome (every merchant screen). */
export function PageHead(props: PageHeadProps) {
    return <Head {...props} />;
}

/**
 * The staff chrome's header plus the slots a screen fills. Wrap the header
 * AND the screen in it: the portals only reach nodes inside the same tree.
 */
export function PageSlots({ head, children }: { head: PageHeadProps; children: ReactNode }) {
    const [targets, setTargets] = useState<PageSlotTargets>({ sentence: null, actions: null });
    // Callback refs, and stable ones: a ref whose identity changed would be
    // called with null and then the node on every render, and each call sets
    // state. Comparing first keeps a repeat call from re-setting the same node.
    const setSentence = useCallback((el: HTMLElement | null) => setTargets((t) => (t.sentence === el ? t : { ...t, sentence: el })), []);
    const setActions = useCallback((el: HTMLElement | null) => setTargets((t) => (t.actions === el ? t : { ...t, actions: el })), []);
    const registry = useMemo<PageSlotRegistry>(() => ({ targets, setSentence, setActions }), [targets, setSentence, setActions]);
    return (
        <PageSlotContext.Provider value={registry}>
            <Head {...head} slots={registry} />
            {children}
        </PageSlotContext.Provider>
    );
}

/**
 * The one sentence under the staff page title. One per screen. With no
 * chrome header to portal into, it is the screen's opening line.
 */
export function PageSentence({ children }: { children: ReactNode }) {
    const slots = useContext(PageSlotContext);
    if (!slots) return <div className={cn(TYPE.ts, "mb-4")}>{children}</div>;
    const el = slots.targets.sentence;
    return el ? createPortal(children, el) : null;
}

/**
 * Extra header actions, placed before the chrome's own (scan picker, Rerun,
 * Share). With no chrome header, they are a row at the top of the screen,
 * so a control that only lives here (an engine filter, a refresh) stays.
 */
export function PageActions({ children }: { children: ReactNode }) {
    const slots = useContext(PageSlotContext);
    if (!slots) return <div className="mb-4 flex min-w-0 max-w-full flex-wrap items-center gap-2">{children}</div>;
    const el = slots.targets.actions;
    return el ? createPortal(children, el) : null;
}
